import type { ReactNode } from 'react';
import { Label } from '@tutero/ui';

import { InfoTip } from '@/lib/InfoTip';

/**
 * One section of Settings: its heading, what it is for behind an ⓘ beside it, a line of where
 * things stand where there is one, its fields and its one footer row, as wide as the fields
 * (`settings-is-a-dialog` D1, D11). A refusal and a success are two pieces of state and
 * two different voices — Flutter drew its one success line in `Colors.red`
 * (`contact_details_section.dart:41-44`) because it had only the one.
 *
 * A refusal is an alert in place, never a toast: the sheet is modal and over the page,
 * and a toast behind it is a refusal nobody reads.
 */
export function SettingsSection({
  title,
  titleHidden = false,
  about,
  summary,
  error,
  done,
  children,
  footer,
  onSubmit,
}: {
  title: string;
  /** For a section whose one field names it: the heading stays for a screen reader only. */
  titleHidden?: boolean;
  /** What the section is for, in the ⓘ's tooltip beside its heading. */
  about?: ReactNode;
  /** Where things stand, on screen: *You sign in as …*. Never an explanation. */
  summary?: ReactNode;
  /** The reason the last attempt was refused, or null. */
  error?: string | null;
  /** What the last attempt did, in this section's own words, or null. */
  done?: string | null;
  children: ReactNode;
  footer: ReactNode;
  onSubmit: () => void;
}) {
  return (
    <form
      /* Its own named region: two sections of the sheet can hold a field of the same name. */
      aria-label={title}
      className="flex flex-col gap-3"
      onSubmit={(event) => {
        event.preventDefault();
        onSubmit();
      }}
    >
      {titleHidden ? (
        <h3 className="sr-only">{title}</h3>
      ) : (
        <div className="flex flex-col gap-1">
          <div className="flex items-center gap-2">
            <h3 className="text-bold-m">{title}</h3>
            {about === undefined ? null : (
              <InfoTip label={`About ${title.toLowerCase()}`}>{about}</InfoTip>
            )}
          </div>
          {summary === undefined ? null : (
            <p className="text-muted-foreground text-regular-s">{summary}</p>
          )}
        </div>
      )}
      {children}
      {error ? (
        <p role="alert" className="text-destructive text-regular-s">
          {error}
        </p>
      ) : null}
      {done ? (
        <p role="status" className="text-regular-s">
          {done}
        </p>
      ) : null}
      <div className="flex flex-col">{footer}</div>
    </form>
  );
}

/** A labelled field, which every section draws the same way; a rule it keeps is an ⓘ beside it. */
export function Field({
  id,
  label,
  labelClassName,
  about,
  children,
}: {
  id: string;
  label: string;
  labelClassName?: string;
  about?: ReactNode;
  children: ReactNode;
}) {
  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-center gap-2">
        <Label htmlFor={id} className={labelClassName}>
          {label}
        </Label>
        {about === undefined ? null : (
          <InfoTip label={`About ${label.toLowerCase()}`}>{about}</InfoTip>
        )}
      </div>
      {children}
    </div>
  );
}
